import { Toaster } from "@landing/components/ui/sonner"
import { GlobalUIProvider } from "@landing/providers/GlobalUIProvider"

import type { QueryClient } from "@tanstack/react-query"
import { createRootRouteWithContext, Outlet } from "@tanstack/react-router"
import React, { Suspense, useEffect, useState } from "react"

// Lazy load devtools only in development
const ReactQueryDevtools = import.meta.env.DEV
  ? React.lazy(() =>
      import("@tanstack/react-query-devtools/production").then((m) => ({
        default: m.ReactQueryDevtools,
      })),
    )
  : () => null

const TanStackRouterDevtools = import.meta.env.DEV
  ? React.lazy(() =>
      import("@tanstack/react-router-devtools").then((m) => ({
        default: m.TanStackRouterDevtools,
      })),
    )
  : () => null

interface RouterContext {
  queryClient: QueryClient
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootRoute,
})

function RootRoute() {
  const [showDevtools, setShowDevtools] = useState(false)

  // Toggle devtools with cmd+ctrl+i
  useEffect(() => {
    function handleKeyPress(event: KeyboardEvent) {
      if (event.metaKey && event.ctrlKey && event.key.toLowerCase() === "i") {
        setShowDevtools((prev) => !prev)
      }
    }

    window.addEventListener("keydown", handleKeyPress)
    return () => window.removeEventListener("keydown", handleKeyPress)
  }, [])

  return (
    <GlobalUIProvider>
      <RootContent showDevtools={showDevtools} />
    </GlobalUIProvider>
  )
}

function RootContent({ showDevtools }: { showDevtools: boolean }) {
  return (
    <>
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="theme-color" content="#001CFF" />
      <div className="mx-auto flex min-h-dvh flex-col border-[#ebf1f4] bg-navy shadow-md overflow-x-hidden">
        {/* Main content */}
        <main className="flex flex-col flex-1 overflow-x-hidden">
          <Outlet />
        </main>

        <Toaster position="bottom-center" />
      </div>

      {/* Dev tools */}
      {showDevtools && (
        <Suspense fallback={null}>
          <TanStackRouterDevtools position="top-left" />
          <ReactQueryDevtools buttonPosition="top-right" />
        </Suspense>
      )}
    </>
  )
}
